import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { usePathParam, useViewport } from '@chilecompra/react-kit'
import { Skeleton, Toast, Typography } from '@chilecompra/react-kit/components'
import { PUBLIC_URL, REACT_APP_SANCTION_LEARN_MORE_URL } from '../../config/settings/environment'
import { onGetSantionDetailsThunk, onGetUserInfoThunk } from './Sanction.actions'
import ProtectByFeatures from '../../components/Security/ProtectByFeatures'
import {
  AlertIcon,
  AlertWrapper,
  Card,
  Column,
  NotFoundImg,
  PrimaryLink,
  Row,
  Separator,
  Strong
} from './SanctionHeadingSection.styles'
import { useAuthorization } from '../AuthorizationProvider/AuthorizationProvider.hooks'
import { USER_TYPE } from '../../config/settings/constant'
import useReCaptcha from '../../modules/hooks/reCaptcha'

/**
 * The Saction HeadingSection's container.
 */
const SanctionHeadingSection = () => {
  const { userType } = useAuthorization()
  const { executeReCaptcha } = useReCaptcha()
  const dispatch = useDispatch()
  const sanctionID = usePathParam('id')
  const {
    size: { isUpToMedium }
  } = useViewport()
  const {
    userInfo: { email, error, fullName, loading, orgName, phone, position, unitName },
    revoke
  } = useSelector(state => state.sanction)
  const { error: sellerError } = useSelector(state => state.basicSellerInformation)
  const isPublicUser = userType === USER_TYPE.PUBLIC

  const executeSanctionDetails = () => {
    if (isNaN(sanctionID) || userType === '') return
    if (userType === USER_TYPE.PUBLIC) {
      const executeGetSantionDetailsThunk = async () => {
        await executeReCaptcha({ action: 'publicInfo' })
        dispatch(onGetSantionDetailsThunk({ id: sanctionID }))
      }
      executeGetSantionDetailsThunk()
    } else {
      dispatch(onGetSantionDetailsThunk({ id: sanctionID }))
    }
  }

  useEffect(() => {
    if (!loading || !isNaN(sanctionID)) {
      return
    }

    dispatch(onGetUserInfoThunk())
  }, [sanctionID])

  useEffect(() => {
    executeSanctionDetails()
  }, [sanctionID, userType])

  useEffect(() => {
    revoke.reloadSanction && executeSanctionDetails()
  }, [revoke])

  return sellerError ? null : (
    <Row margin="14px 0 0">
      {!error && (
        <Column xs={12} sm={12} md={9} padding={isUpToMedium ? '0 27px 0 0' : '0'}>
          {isNaN(sanctionID) && (
            <Typography variant="body2">
              El acto administrativo de respaldo es obligatorio para poder registrar una sanción por incumplimiento. Si
              es una sanción proveniente de un contrato, deberás registrarla en el módulo Gestión de Contrato.
            </Typography>
          )}

          {isNaN(sanctionID) && <Separator />}

          {loading && !isPublicUser && (
            <Row>
              <Column md={4} sm={6} xs={6}>
                <Typography fontWeight="bold" variant="body2">
                  Nombre del organismo público
                </Typography>
                <Skeleton width="90%" />
              </Column>
              <Column md={4} sm={6} xs={6}>
                <Typography fontWeight="bold" variant="body2">
                  Usuario que ingresa sanción
                </Typography>
                <Skeleton width="90%" />
              </Column>

              <Column md={4} sm={6} xs={6} padding={!isUpToMedium ? '26px 5px 0' : '0 5px'}>
                <Typography fontWeight="bold" variant="body2">
                  Cargo
                </Typography>
                <Skeleton width="90%" />
              </Column>
              <Column md={4} sm={6} xs={6} padding="26px 5px 0">
                <Typography fontWeight="bold" variant="body2">
                  Teléfono
                </Typography>
                <Skeleton width="90%" />
              </Column>

              <Column md={4} sm={6} xs={6} padding="26px 5px 0">
                <Typography fontWeight="bold" variant="body2">
                  Mail
                </Typography>
                <Skeleton width="90%" />
              </Column>
            </Row>
          )}

          {!loading && orgName && (
            <Row>
              <Column md={4} sm={6} xs={6}>
                <Typography fontWeight="bold" variant="body2">
                  Nombre del organismo público
                </Typography>
                <Typography variant="body2">{orgName}</Typography>
              </Column>
              {isPublicUser && unitName && (
                <Column md={4} sm={6} xs={6}>
                  <Typography fontWeight="bold" variant="body2">
                    Nombre de la unidad de compra
                  </Typography>
                  <Typography variant="body2">{unitName}</Typography>
                </Column>
              )}
              {!isPublicUser && (
                <>
                  <Column md={4} sm={6} xs={6}>
                    <Typography fontWeight="bold" variant="body2">
                      Usuario que ingresa sanción
                    </Typography>
                    <Typography variant="body2">{fullName}</Typography>
                  </Column>
                  <Column md={4} sm={6} xs={6} padding={!isUpToMedium ? '26px 5px 0' : '0 5px'}>
                    <Typography fontWeight="bold" variant="body2">
                      Cargo
                    </Typography>
                    <Typography variant="body2">{position}</Typography>
                  </Column>
                  <Column md={4} sm={6} xs={6} padding="26px 5px 0">
                    <Typography fontWeight="bold" variant="body2">
                      Teléfono
                    </Typography>
                    <Typography variant="body2">{phone}</Typography>
                  </Column>
                  <Column md={4} sm={6} xs={6} padding="26px 5px 0">
                    <Typography fontWeight="bold" variant="body2">
                      Mail
                    </Typography>
                    <Typography variant="body2">{email}</Typography>
                  </Column>
                </>
              )}
            </Row>
          )}

          {!loading && orgName && <Separator />}
          {loading && <Separator />}
        </Column>
      )}

      {!error && isNaN(sanctionID) && (
        <ProtectByFeatures rule={features => features['com.seller/contractual.behavior.utm.validation']}>
          <Column xs={12} sm={12} md={3} padding="0">
            <Toast disableIcon padding="2px 18px" severity="info">
              <AlertWrapper>
                <AlertIcon />
                <Typography variant="body2">
                  Si la sanción que quieres registrar proviene de un <Strong>contrato o es mayor a 1000 UTM</Strong>,
                  debes registrarla directamente en su ficha de contrato, en el menú Contratos de Mercado Público.
                </Typography>
                <PrimaryLink href={REACT_APP_SANCTION_LEARN_MORE_URL} target="_blank">
                  Saber más
                </PrimaryLink>
              </AlertWrapper>
            </Toast>
          </Column>
        </ProtectByFeatures>
      )}

      {error && !loading && (
        <Column xs={12} sm={12} md={9} padding={isUpToMedium ? '0 27px 27px 0' : '0'}>
          {error.reason === 'SOMETHING_WENT_WRONG_ERROR' && (
            <Toast severity="error" title="Lo sentimos, estamos presentando problemas con nuestro servidor.">
              En estos momentos tenemos problemas para disponibilizar la información. Por favor, consulta en unos
              minutos más.
            </Toast>
          )}

          {error.reason === 'DOES_NOT_EXIST_ERROR' && (
            <Card enableBorder enableLine padding="50px 0">
              <NotFoundImg src={`${PUBLIC_URL}/assets/images/notFound.svg`} />
              <Typography fontWeight="bold" margin="8px 0" variant="h4">
                No se encontraron resultados para tu búsqueda
              </Typography>
              <Typography color="black3" fontSize="14px" lineHeight="18px" variant="body1">
                La sanción que buscas no se encuentra en Mercado
                <br />
                Público. Intenta con otra palabra clave u otro
                <br />
                criterio de búsqueda.
              </Typography>
            </Card>
          )}
        </Column>
      )}
    </Row>
  )
}
export default SanctionHeadingSection
